/**
 * The Prices tab's field keys and the mapping from a server field to them (TMMPDM-0089). A server
 * field names WHICH input failed; the words are the catalog's (TMM-B3), never the server's.
 * TMMPDM-0090: the same words for a version's `violations` (tmm-api 0155), keyed on the field alone.
 */
import { ApiError } from '@/api/errors';

export const PRICE_KEYS = [
  'baseCostMinor',
  'baseWholesaleMinor',
  'baseMinor',
  'annualBaseWholesaleMinor',
  'annualBaseMinor',
  'guestsIncluded',
  'guestCost',
  'guestWholesale',
  'guestRetail',
  'connectionsIncluded',
  'connectionCost',
  'connectionWholesale',
  'connectionRetail',
  'aiWholesaleMarkupBp',
  'aiRetailMarkupBp',
  'aiMonthlyCredit',
  'aiCreditPlatformShareBp',
  'aiFirstPurchaseMinor',
  'aiRechargeTriggerMinor',
  'aiRechargeAmountMinor',
  'trialDaysNewSubscriber',
  'trialDaysGuestConversion',
] as const;

export type PriceKey = (typeof PRICE_KEYS)[number];

const DIRECT: Partial<Record<string, PriceKey>> = {
  baseCostMinor: 'baseCostMinor',
  baseWholesaleMinor: 'baseWholesaleMinor',
  baseMinor: 'baseMinor',
  annualBaseWholesaleMinor: 'annualBaseWholesaleMinor',
  annualBaseMinor: 'annualBaseMinor',
  aiWholesaleMarkupBp: 'aiWholesaleMarkupBp',
  aiRetailMarkupBp: 'aiRetailMarkupBp',
  aiMarkupBp: 'aiRetailMarkupBp',
  aiMonthlyCreditMinor: 'aiMonthlyCredit',
  aiMonthlyCreditCostMinor: 'aiMonthlyCredit',
  aiCreditPlatformShareBp: 'aiCreditPlatformShareBp',
  aiFirstPurchaseMinor: 'aiFirstPurchaseMinor',
  aiRechargeTriggerMinor: 'aiRechargeTriggerMinor',
  aiRechargeAmountMinor: 'aiRechargeAmountMinor',
  trialDaysNewSubscriber: 'trialDaysNewSubscriber',
  trialDaysGuestConversion: 'trialDaysGuestConversion',
};

const BAND = /^(seatBands|connectedAccountBands)(?:\[\d+\])?(?:\.(\w+))?$/;

/** The input a server field belongs to; `null` when no input on the tab holds it. */
export function priceKeyOf(field: string): PriceKey | null {
  const direct = DIRECT[field];
  if (direct) return direct;
  const band = BAND.exec(field);
  if (!band) return null;
  const guests = band[1] === 'seatBands';
  const column = band[2] ?? '';
  if (column.startsWith('wholesale')) return guests ? 'guestWholesale' : 'connectionWholesale';
  if (column.startsWith('cost')) return guests ? 'guestCost' : 'connectionCost';
  if (column !== '') return guests ? 'guestRetail' : 'connectionRetail';
  return guests ? 'guestsIncluded' : 'connectionsIncluded';
}

type Copy = (key: string, fallback?: string, values?: Record<string, string | number>) => string;

/** The input's label in catalog words (the monthly credit's at the customer price). */
export function priceLabel(key: PriceKey, t: Copy): string {
  switch (key) {
    case 'baseCostMinor':
      return t('rateCard.prices.ourCost', 'Our cost');
    case 'baseWholesaleMinor':
      return t('rateCard.prices.wholesale', 'Wholesale');
    case 'baseMinor':
      return t('rateCard.prices.retail', 'Retail');
    case 'annualBaseWholesaleMinor':
      return t('rateCard.prices.annualWholesale', 'Wholesale per year');
    case 'annualBaseMinor':
      return t('rateCard.prices.annualRetail', 'Retail per year');
    case 'guestsIncluded':
      return t('rateCard.prices.guestsIncluded', 'Guests included');
    case 'guestCost':
      return t('rateCard.prices.perGuestCost', 'Our cost per guest');
    case 'guestWholesale':
      return t('rateCard.prices.perGuestWholesale', 'Wholesale per guest');
    case 'guestRetail':
      return t('rateCard.prices.perGuestRetail', 'Retail per guest');
    case 'connectionsIncluded':
      return t('rateCard.prices.connectionsIncluded', 'Bank connections included');
    case 'connectionCost':
      return t('rateCard.prices.perConnectionCost', 'Our cost per connection');
    case 'connectionWholesale':
      return t('rateCard.prices.perConnectionWholesale', 'Wholesale per connection');
    case 'connectionRetail':
      return t('rateCard.prices.perConnectionRetail', 'Retail per connection');
    case 'aiWholesaleMarkupBp':
      return t('rateCard.prices.aiWholesaleMarkup', 'Wholesale markup on provider cost (%)');
    case 'aiRetailMarkupBp':
      return t('rateCard.prices.aiRetailMarkup', 'Retail markup on wholesale (%)');
    case 'aiMonthlyCredit':
      return t('rateCard.prices.creditAtRetail', 'Monthly AI credit, at the customer price');
    case 'aiCreditPlatformShareBp':
      return t('rateCard.prices.creditShare', 'Share of the credit we absorb (%)');
    case 'aiFirstPurchaseMinor':
      return t('customerRateCards.form.aiFirstPurchase', 'AI first purchase');
    case 'aiRechargeTriggerMinor':
      return t('customerRateCards.form.aiRechargeTrigger', 'Recharge when the balance falls below');
    case 'aiRechargeAmountMinor':
      return t('rateCard.prices.rechargeAmount', 'Wallet recharge amount');
    case 'trialDaysNewSubscriber':
      return t('rateCards.form.trialNew', 'Trial days — new subscriber');
    default:
      return t('rateCards.form.trialGuest', 'Trial days — guest conversion');
  }
}

/**
 * The catalog's words for the publish rule a server field breaks, chosen by the field alone
 * (TMMAPI-0155 `publishViolations`). A wholesale field can break two rules (above 0 when its retail
 * is, and at most the retail), so its line names both; the AI wholesale markup must be above 0;
 * the other fields must not be negative. A bare band (`seatBands[0]`) can break several rules, so
 * its line is neutral. The server's own reason is never shown (TMM-B3).
 */
function ruleFor(field: string, t: Copy): string {
  const band = BAND.exec(field);
  if (band && band[2] === undefined) {
    return t('rateCard.prices.rule.checkBand', "Check this band's prices.");
  }
  if (field === 'aiWholesaleMarkupBp') {
    return t('rateCard.prices.rule.aboveZero', 'Must be more than zero.');
  }
  if (/wholesale/i.test(field)) {
    return t('rateCard.prices.rule.wholesale', 'Must be above zero and at most the retail price.');
  }
  return t('rateCard.prices.rule.notNegative', 'Must be zero or greater.');
}

/** Which band tables the Prices tab edits (the "included + per unit" shape). */
export interface EditableBands {
  guests: boolean;
  connections: boolean;
}

/**
 * 409 `RATE_CARD_INVALID` (TMMAPI-0154/0155): `details.fields[]` of `{field, reason}`. Each failing
 * field maps to its input with the catalog's words for the rule it breaks; the server's `reason` is
 * never shown. A field with no input is returned under `unplaced` so the caller still says
 * something — including a band field of a tiered table (`editable` false), whose bands the Prices
 * tab shows read-only.
 */
export function publishViolations(
  error: unknown,
  t: Copy,
  editable: EditableBands = { guests: true, connections: true },
): { fields: Partial<Record<PriceKey, string>>; unplaced: string[] } | null {
  if (!(error instanceof ApiError) || String(error.code) !== 'RATE_CARD_INVALID') return null;
  const raw = error.details.backend?.['fields'];
  const fields: Partial<Record<PriceKey, string>> = {};
  const unplaced: string[] = [];
  if (!Array.isArray(raw)) return { fields, unplaced };
  for (const entry of raw as unknown[]) {
    if (entry === null || typeof entry !== 'object') continue;
    const field = (entry as Record<string, unknown>)['field'];
    if (typeof field !== 'string') continue;
    const key = priceKeyOf(field);
    const band = BAND.exec(field);
    const tiered =
      band !== null && !(band[1] === 'seatBands' ? editable.guests : editable.connections);
    if (key === null || tiered) {
      unplaced.push(field);
      continue;
    }
    fields[key] = ruleFor(field, t);
  }
  return { fields, unplaced };
}

const BAND_INDEX = /^(seatBands|connectedAccountBands)\[(\d+)\]/;

/**
 * A version's `violations` (TMMAPI-0155) as lines in catalog words, one per violation in the order
 * sent: the input's label (a tiered table's band by its section and position), then the rule's
 * words. Keyed on `field` alone; `rule` is the server's text and is never read. Adds no rule.
 */
export function violationLines(
  violations: readonly { field: string }[],
  t: Copy,
  editable: EditableBands,
): string[] {
  return violations.map(({ field }) => {
    const band = BAND_INDEX.exec(field);
    const guests = band?.[1] === 'seatBands';
    const key = priceKeyOf(field);
    const label =
      band &&
      (BAND.exec(field)?.[2] === undefined || !(guests ? editable.guests : editable.connections))
        ? t('rateCard.violations.band', '{{section}}, band {{n, number}}', {
            section: guests
              ? t('rateCard.prices.section.guests', 'Guests')
              : t('rateCard.prices.section.connections', 'Bank connections'),
            n: Number(band[2]) + 1,
          })
        : key !== null
          ? priceLabel(key, t)
          : t('rateCard.violations.other', 'Another price on this version');
    // A field with no input and no band names no rule the console knows: neutral words (TMM-B3).
    const message =
      key === null && !band
        ? t('rateCard.violations.checkValue', 'Check this value.')
        : ruleFor(field, t);
    return t('rateCard.violations.item', '{{label}}: {{message}}', { label, message });
  });
}
